import { useAuth } from "@clerk/nextjs";
import MuxVideo from "@mux/mux-video-react";
import clsx from "clsx";
import { useRouter } from "next/compat/router";
import dynamic from "next/dynamic";
import Image from "next/image";
import posthog from "posthog-js";

import { useAccess, useUserAction } from "~/hooks";
import useCta from "~/hooks/useCta";
import useSale from "~/hooks/useSale";
import useTrackCTA from "~/hooks/useTrackCTA";
import saleAnimation from "~/public/lottie-animations/sale.json";
import { FEATURES } from "~/utils/constants";

const Lottie = dynamic(
	() => import("lottie-react").then((mod) => mod.default),
	{ ssr: false },
);

const ExpressCard = () => {
	const { isLoaded, userId } = useAuth();
	const { isLoadingSale, sale } = useSale();
	const { setUserAction } = useUserAction();
	const { isLoadingAccess, isPaused, isMember } = useAccess();
	const router = useRouter();
	const trackCTA = useTrackCTA();
	const { cta, isCtaEnabled, hasCta } = useCta();

	if (
		!isLoaded ||
		isLoadingAccess ||
		isPaused ||
		isLoadingSale ||
		(isMember && !sale?.isAnnualUpgrade)
	)
		return null;

	const colorToStylesMap: {
		[key: string]: {
			background: string;
			text: string;
			secondaryText: string;
			button?: string;
		};
	} = {
		control: {
			background: "bg-white dark:bg-slate-700",
			text: "text-sky-700 dark:text-sky-200",
			secondaryText: "text-slate-700 dark:text-slate-300",
			button:
				"bg-gradient-to-r from-sky-500 via-sky-700 to-sky-500 hover:from-sky-600 hover:via-sky-800 hover:to-sky-600 text-white shadow-slate-500 animated-background",
		},
		blue: {
			background:
				"bg-gradient-to-r from-sky-500 via-sky-700 to-sky-500 animated-background-slow",
			text: "text-white",
			secondaryText: "text-white",
			button: "bg-white text-slate-700 hover:bg-slate-100",
		},
		dark: {
			background:
				"bg-gradient-to-r from-yellow-950 via-sky-950 to-yellow-950 animated-background",
			text: "text-white",
			secondaryText: "text-white",
			button: "bg-white text-yellow-800 hover:bg-slate-100",
		},
	};

	const selectedStyles = userId
		? sale
			? colorToStylesMap.dark // If a sale is on use dark theme
			: colorToStylesMap.blue // If user is logged in (subscription) use blue theme
		: colorToStylesMap.control; // If logged out (signup) use white control theme

	return (
		<div
			className={clsx(
				"group relative rounded-xl border px-4 py-3 shadow shadow-slate-100 dark:bg-slate-700/50 dark:shadow-slate-900",
				selectedStyles.background,
			)}
		>
			<div className="flex flex-col items-start gap-2.5">
				<div className="flex flex-col gap-0.5">
					{sale && (
						<div className="h-10 overflow-hidden">
							<Lottie
								animationData={saleAnimation}
								loop
								className="-my-4 laptop:w-20 w-16 dark:brightness-90"
							/>
						</div>
					)}
					<h3 className={clsx("font-semibold text-lg", selectedStyles.text)}>
						{cta.title}
					</h3>

					<div
						className={clsx(
							selectedStyles.secondaryText,
							"text-sm dark:text-white",
						)}
					>
						{cta.description}
					</div>
				</div>
				{cta.enableMedia === "video" && cta.video && !sale ? (
					<div className="flex w-full overflow-hidden rounded-lg">
						<MuxVideo
							poster={cta.image}
							playbackId={cta.video}
							autoPlay
							muted
						/>
					</div>
				) : cta.enableMedia === "image" && cta.image && !sale ? (
					<Image
						src={cta.image}
						alt={`${cta.title} Image`}
						className={"rounded-lg"}
						height={250}
						width={380}
					/>
				) : (
					<ul className="flex w-full flex-col gap-1.5">
						{FEATURES.map((feature) => (
							<li
								key={feature.title}
								className="flex items-center gap-2 text-sm"
							>
								<feature.icon
									className={clsx(
										"size-4 shrink-0 bg-transparent!",
										selectedStyles.secondaryText,
									)}
								/>
								<span
									className={clsx(
										"font-medium leading-snug",
										selectedStyles.secondaryText,
									)}
								>
									{feature.title}
								</span>
							</li>
						))}
					</ul>
				)}
				<div className="flex w-full">
					<button
						onClick={() => {
							if (userId) {
								posthog.capture("cta:membership_card_clicked", {
									hasCta,
									isCtaEnabled,
								});
							} else {
								posthog.capture("cta:signup_card_clicked", {
									hasCta,
									isCtaEnabled,
								});
							}

							if (cta.redirectButton) {
								router?.push(cta.buttonRedirectURL!);
								return;
							}

							trackCTA(cta.userAction, "Express Card");
							setUserAction(cta.userAction);
						}}
						className={clsx(
							"inset-ring-1 inset-ring-gray-500/80 w-full rounded-full px-4 py-2 font-medium shadow-md",
							selectedStyles.button,
						)}
					>
						{cta.buttonText}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ExpressCard;
